import { DestroyRef, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FirmwareByIdResponse,
  FirmwareClient,
  UpdateFirmwareRequest,
} from '../../../core/openapi/bff-admin-api';
import { ToastService } from '@ui-lib/components';
import { TranslateService } from '@ngx-translate/core';
import { catchError, finalize, map, Observable, tap, throwError } from 'rxjs';
import { outputLocalizationError } from '../../../shared/utils/output-error.function';
import { LoadingState } from '../../../shared/types/loading.type';

@Injectable()
export class FirmwareManagementService {
  private readonly firmwareClient = inject(FirmwareClient);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _loadingState: WritableSignal<LoadingState> = signal<LoadingState>(
    LoadingState.IDLE,
  );

  public readonly loadingState: Signal<LoadingState> = this._loadingState.asReadonly();

  public createFirmware(
    name: string,
    version: string,
    equipmentModelIds: string[],
    file: Blob,
    comment?: string,
  ): Observable<void> {
    this._loadingState.set(LoadingState.LOADING);

    return this.firmwareClient.v1FirmwarePost(name, version, equipmentModelIds, file, comment).pipe(
      map((): void => undefined),
      tap(() => {
        this.toastService.success({
          message: this.translateService.instant(
            'monitoring.firmware.modal.toast.firmware-created',
            { name },
          ),
        });
      }),
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.firmware.modal.toast.firmware-creation-failed',
            {
              name,
              error: this.translateService.instant(
                outputLocalizationError(error, 'monitoring.firmware.modal.error', 'errors.unknown'),
              ),
            },
          ),
        });
        return throwError(() => error);
      }),
      finalize(() => this._loadingState.set(LoadingState.READY)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  public loadFirmwareById(firmwareId: string): Observable<FirmwareByIdResponse> {
    if (firmwareId.length === 0) {
      return throwError(() => new Error('No firmware id provided'));
    }

    this._loadingState.set(LoadingState.LOADING);

    return this.firmwareClient.v1FirmwareIdGet(firmwareId).pipe(
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.firmware.modal.toast.firmware-reading-failed',
            {
              error: this.translateService.instant(
                outputLocalizationError(error, 'monitoring.firmware.modal.error', 'errors.unknown'),
              ),
            },
          ),
        });
        return throwError(() => error);
      }),
      finalize(() => this._loadingState.set(LoadingState.READY)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  public updateFirmware(request: UpdateFirmwareRequest): Observable<void> {
    if (!request.id) {
      return throwError(() => new Error('No id provided for a firmware update operation.'));
    }

    this._loadingState.set(LoadingState.LOADING);

    return this.firmwareClient.v1FirmwarePut(request).pipe(
      map((): void => undefined),
      tap(() => {
        this.toastService.success({
          message: this.translateService.instant(
            'monitoring.firmware.modal.toast.firmware-updated',
            { name: request.name },
          ),
        });
      }),
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.firmware.modal.toast.firmware-update-failed',
            {
              name: request.name,
              error: this.translateService.instant(
                outputLocalizationError(error, 'monitoring.firmware.modal.error', 'errors.unknown'),
              ),
            },
          ),
        });

        return throwError(() => error);
      }),
      finalize(() => this._loadingState.set(LoadingState.READY)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  public deleteFirmware(id: string, name: string): Observable<void> {
    this._loadingState.set(LoadingState.LOADING);

    return this.firmwareClient.v1FirmwareIdDelete(id).pipe(
      map((): void => undefined),
      tap(() => {
        this.toastService.success({
          message: this.translateService.instant(
            'monitoring.firmware.modal.toast.firmware-removed',
            { name },
          ),
        });
      }),
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.firmware.modal.toast.firmware-deletion-failed',
            {
              error: this.translateService.instant(
                outputLocalizationError(error, 'monitoring.firmware.modal.error', 'errors.unknown'),
              ),
            },
          ),
        });
        return throwError(() => error);
      }),
      finalize(() => this._loadingState.set(LoadingState.READY)),
      takeUntilDestroyed(this.destroyRef),
    );
  }
}
